/**
 * TaxSettings - MWST-Einstellungen
 */
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Package, Save } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TaxSettings = () => {
  const [taxSettings, setTaxSettings] = useState({
    default_tax_rate: 8.1,
    tax_number: '',
    tax_included_in_prices: true,
    show_tax_breakdown: true
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadTaxSettings();
  }, []);

  const loadTaxSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/settings/tax`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data) {
        setTaxSettings(response.data);
      }
    } catch (error) {
      console.error('Error loading tax settings:', error);
    }
  };

  const handleSaveTaxSettings = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/settings/tax`, taxSettings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('MWST-Einstellungen gespeichert');
    } catch (error) {
      toast.error('Fehler beim Speichern');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Package size={24} />
          MWST-Einstellungen
        </CardTitle>
        <CardDescription>
          Konfigurieren Sie die Mehrwertsteuer-Einstellungen für Ihre Services
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <Label htmlFor="tax_rate">Standard MWST-Satz (%)</Label>
            <Input
              id="tax_rate"
              type="number"
              step="0.1"
              min="0"
              max="100"
              value={taxSettings.default_tax_rate}
              onChange={(e) => setTaxSettings({...taxSettings, default_tax_rate: parseFloat(e.target.value) || 0})}
            />
            <p className="text-xs text-gray-500">
              Standard-Mehrwertsteuersatz für neue Services (z.B. 8.1 für Schweiz)
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="tax_number">Steuernummer (optional)</Label>
            <Input
              id="tax_number"
              type="text"
              placeholder="CHE-123.456.789"
              value={taxSettings.tax_number}
              onChange={(e) => setTaxSettings({...taxSettings, tax_number: e.target.value})}
            />
            <p className="text-xs text-gray-500">
              Ihre Umsatzsteuer-Identifikationsnummer für Rechnungen
            </p>
          </div>
        </div>

        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <Label htmlFor="tax_included">MWST in Preisen enthalten</Label>
              <p className="text-xs text-gray-500">
                Sind die angezeigten Preise Brutto- (inkl. MWST) oder Nettopreise?
              </p>
            </div>
            <Switch
              id="tax_included"
              checked={taxSettings.tax_included_in_prices}
              onCheckedChange={(checked) => setTaxSettings({...taxSettings, tax_included_in_prices: checked})}
            />
          </div>

          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <Label htmlFor="show_breakdown">MWST-Aufschlüsselung anzeigen</Label>
              <p className="text-xs text-gray-500">
                MWST-Betrag separat auf Rechnungen und Buchungsbestätigungen anzeigen
              </p>
            </div>
            <Switch
              id="show_breakdown"
              checked={taxSettings.show_tax_breakdown}
              onCheckedChange={(checked) => setTaxSettings({...taxSettings, show_tax_breakdown: checked})}
            />
          </div>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-sm text-blue-800">
            ℹ️ <strong>Hinweis:</strong> Diese Einstellungen gelten als Standard für neue Services. 
            Bestehende Services behalten ihre individuellen MWST-Einstellungen.
          </p>
        </div>

        <div className="pt-4 border-t flex justify-end">
          <Button onClick={handleSaveTaxSettings} disabled={loading}>
            <Save size={16} className="mr-2" />
            MWST-Einstellungen speichern
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default TaxSettings;
